
// Revisado: 2025-12-18 I18n Updated
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '../../../routes';
import { useAuth } from '../../../context/AuthContext';
import { API_BASE_URL } from '../../../constants';
import { useTranslation } from 'react-i18next';


const SupportEdit = ({ initialData, isEditMode = false, onUpdate }) => {
    const { isAdmin } = useAuth();
    const navigate = useNavigate();
    const { t } = useTranslation();

    // States
    const [characters, setCharacters] = useState([]);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    // Form Data
    const [formData, setFormData] = useState({
        char_id: '',
        support_id: '',
        rarity: 'SSR',
        Support_Type: 'Speed',
        name_en: '',
        name_jp: '',
        release_en: '',
        release_jp: ''
    });

    // Populate data in Edit Mode
    useEffect(() => {
        if (isEditMode && initialData) {
            setFormData({
                char_id: initialData.char_id || '',
                support_id: initialData.support_id || '',
                rarity: initialData.rarity || 'SSR',
                Support_Type: initialData.Support_Type || 'Speed',
                name_en: initialData.name_en || '',
                name_jp: initialData.name_jp || '',
                release_en: initialData.release_en ? initialData.release_en.split('T')[0] : '', // Format YYYY-MM-DD
                release_jp: initialData.release_jp ? initialData.release_jp.split('T')[0] : ''
            });
        }
    }, [isEditMode, initialData]);

    // Options
    const rarities = ['SSR', 'SR', 'R'];
    const types = ['Speed', 'Stamina', 'Power', 'Guts', 'Wit', 'Pal', 'Group'];

    // 1. Verify Admin & Fetch Characters
    useEffect(() => {
        if (!isAdmin()) {
            navigate(ROUTES.CHARACTERS_SUPPORT);
            return;
        }

        const fetchCharacters = async () => {
            try {
                const response = await fetch(`${API_BASE_URL}/characters`);
                if (!response.ok) throw new Error(t('SupportEdit.fetch_chars_error'));
                const data = await response.json();

                // Sort by name_en
                const sorted = data.sort((a, b) =>
                    (a.name_en || a.name_ja).localeCompare(b.name_en || b.name_ja)
                );

                setCharacters(sorted);
                setLoading(false);
            } catch (err) {
                setError(err.message);
                setLoading(false);
            }
        };

        fetchCharacters();
    }, [isAdmin, navigate, t]);

    // Handle Input Change
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // Handle Submit
    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        setError(null);
        setSuccess(null);

        // Validation: support_id must be provided manually
        if (!formData.support_id) {
            setError(t('SupportEdit.validation_support_id'));
            setSubmitting(false);
            return;
        }

        const payload = {
            ...formData,
            char_id: parseInt(formData.char_id),
            support_id: parseInt(formData.support_id),
            name_en: formData.name_en || null,
            name_jp: formData.name_jp, // Required
            release_en: formData.release_en || null,
            release_jp: formData.release_jp || null
        };

        try {
            const url = isEditMode
                ? `${API_BASE_URL}/api/supports/${formData.support_id}`
                : `${API_BASE_URL}/api/supports`;

            const method = isEditMode ? 'PUT' : 'POST';

            const response = await fetch(url, {
                method: method,
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(payload)
            });

            if (!response.ok) {
                const resData = await response.json();
                throw new Error(resData.error || t('SupportEdit.save_error'));
            }

            const resData = await response.json();

            if (isEditMode) {
                setSuccess(t('SupportEdit.save_success_update'));
                if (onUpdate) onUpdate(); // Refresh parent data
            } else {
                setSuccess(t('SupportEdit.save_success_create', { id: resData.id }));
                window.scrollTo(0, 0);
            }

        } catch (err) {
            setError(err.message);
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) return <div className="p-8 text-center">{t('SupportEdit.loading')}</div>;

    return (
        <div className="max-w-4xl mx-auto pt-24 pb-24 px-4">
            {!isEditMode && (
                <h1 className="text-3xl font-bold mb-6 text-gray-800 border-b pb-2">{t('SupportEdit.title_create')}</h1>
            )}

            {error && (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                    {error}
                </div>
            )}

            {success && (
                <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
                    {success}
                </div>
            )}

            <form onSubmit={handleSubmit}>

                {/* 1. Character Selection & Support ID */}
                <div className="flex flex-col md:flex-row gap-4 mb-4">
                    <div className="w-full md:w-1/2">
                        <label className="block text-gray-700 text-sm font-bold mb-2">{t('SupportEdit.label_char')}</label>
                        <select
                            name="char_id"
                            value={formData.char_id}
                            onChange={handleChange}
                            required
                            className="shadow border rounded w-full py-2 px-3 text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                            <option value="">{t('SupportEdit.select_char_placeholder')}</option>
                            {characters.map(char => (
                                <option key={char.char_id} value={char.char_id}>
                                    {char.name_en} ({char.name_ja})
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="w-full md:w-1/2">
                        <label className="block text-gray-700 text-sm font-bold mb-2">{t('SupportEdit.label_support_id')}</label>
                        <input
                            type="number"
                            name="support_id"
                            value={formData.support_id}
                            onChange={handleChange}
                            required
                            readOnly={isEditMode} // Lock ID in Edit Mode
                            placeholder="e.g. 30001"
                            className={`shadow border rounded w-full py-2 px-3 text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 ${isEditMode ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : ''}`}
                        />
                    </div>
                </div>

                {/* 2. Rarity & Type */}
                <div className="flex flex-col md:flex-row gap-4 mb-4">
                    <div className="w-full md:w-1/2">
                        <label className="block text-gray-700 text-sm font-bold mb-2">Rarity</label>
                        <select
                            name="rarity"
                            value={formData.rarity}
                            onChange={handleChange}
                            className="shadow border rounded w-full py-2 px-3 text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                            {rarities.map(r => <option key={r} value={r}>{r}</option>)}
                        </select>
                    </div>

                    <div className="w-full md:w-1/2">
                        <label className="block text-gray-700 text-sm font-bold mb-2">{t('SupportEdit.label_support_type')}</label>
                        <select
                            name="Support_Type"
                            value={formData.Support_Type}
                            onChange={handleChange}
                            className="shadow border rounded w-full py-2 px-3 text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                            {types.map(t => <option key={t} value={t}>{t}</option>)}
                        </select>
                    </div>
                </div>

                {/* 3. Names */}
                <div className="mb-4">
                    <label className="block text-gray-700 text-sm font-bold mb-2">{t('SupportEdit.label_name_jp')}</label>
                    <input
                        type="text"
                        name="name_jp"
                        value={formData.name_jp}
                        onChange={handleChange}
                        required
                        className="shadow border rounded w-full py-2 px-3 text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                </div>

                <div className="mb-4">
                    <label className="block text-gray-700 text-sm font-bold mb-2">{t('SupportEdit.label_name_en')}</label>
                    <input
                        type="text"
                        name="name_en"
                        value={formData.name_en}
                        onChange={handleChange}
                        className="shadow border rounded w-full py-2 px-3 text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                </div>

                {/* 4. Dates */}
                <div className="flex flex-col md:flex-row gap-4 mb-6">
                    <div className="w-full md:w-1/2">
                        <label className="block text-gray-700 text-sm font-bold mb-2">{t('SupportEdit.label_release_jp')}</label>
                        <input
                            type="date"
                            name="release_jp"
                            value={formData.release_jp}
                            onChange={handleChange}
                            className="shadow border rounded w-full py-2 px-3 text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                    </div>
                    <div className="w-full md:w-1/2">
                        <label className="block text-gray-700 text-sm font-bold mb-2">{t('SupportEdit.label_release_global')}</label>
                        <input
                            type="date"
                            name="release_en"
                            value={formData.release_en}
                            onChange={handleChange}
                            className="shadow border rounded w-full py-2 px-3 text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                    </div>
                </div>

                {/* Buttons */}
                <div className="flex items-center justify-end gap-4">
                    {!isEditMode && (
                        <button
                            type="button"
                            onClick={() => navigate(ROUTES.CHARACTERS_SUPPORT)}
                            className="text-gray-600 hover:text-gray-800 font-bold py-2 px-4 rounded focus:outline-none"
                        >
                            {t('SupportEdit.button_cancel')}
                        </button>
                    )}
                    <button
                        type="submit"
                        disabled={submitting}
                        className={`bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-6 rounded focus:outline-none focus:shadow-outline transition-all ${submitting ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                        {submitting ? t('SupportEdit.button_saving') : (isEditMode ? t('SupportEdit.button_update') : t('SupportEdit.button_create'))}
                    </button>
                </div>

            </form>
        </div>
    );
};

export default SupportEdit;
// Revisado: 2025-12-16
